import React, { FC, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as CopyIcon } from '@blink/assets/src/icons/copy.svg';
import { ButtonStroke } from '@blink/components/src/Button/ButtonStroke';
import { ButtonSize } from '@blink/components/src/Button/Button.types';
import { TextButton } from '@blink/components';
import { WalletHelper } from '@blink/components/src/utils';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Etherscan } from '@blink/assets/src/icons/etherscan.svg';
import { ReactComponent as Loader } from '@blink/assets/src/icons/loaderDash.svg';

import { usePoolDetailsStyle } from './Pool.style';

import { Store } from '@/shared/store';
import { ClientPoolDefault } from '@/shared/store/agreements';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { ClientPool, ROLE, Token } from '@/shared/api/borrow';
import { getAgreementsAction, getClientPoolByIdAction } from '@/shared/store/agreements/actions';
import { PoolDetailsTabs } from '@/widgets/PoolDetailsTabs';
import {
    AssetIcon,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
} from '@/shared/ui';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';

export const enum ContentListType {
    DESCRIPTION = 'description',
    ALLOWED = 'allowed',
}

export const Pool: FC = () => {
    const classes = usePoolDetailsStyle();
    const { address } = useParams();
    const [searchParams] = useSearchParams();
    const role = searchParams.get('role');
    const [pool, setPool] = useState<ClientPool>(ClientPoolDefault);
    const [leverageToken, setLeverageToken] = useState<Token>({} as Token);

    const dispatch = useDispatch();

    const { tokens, clientPool, clientPoolLoading, type, wallet } = useSelector((state: Store) => ({
        tokens: state.tokens.agreementTokens,
        clientPool: state.agreements.clientPool,
        clientPoolLoading: state.agreements.clientPoolLoading,
        type: state.wallets.active.type,
        wallet: state.wallets.active.address,
    }));

    useEffect(() => {
        if (clientPool.address !== address) {
            dispatch(getClientPoolByIdAction(address!));
        } else {
            const currentToken = tokens.find((item) => item.id === clientPool.leverageTokenId);

            setLeverageToken(currentToken!);
            setPool(clientPool);
        }
    }, [type, clientPool, tokens]);

    useEffect(() => {
        if (!tokens.length && wallet) {
            dispatch(
                getAgreementsAction({
                    role: role as ROLE,
                    withoutCache: true,
                }),
            );
        }
    }, [tokens, wallet]);

    const handleCopyAddress = useCopyToClipboard(address || '');

    const refreshAgreements = async () =>
        dispatch(
            getAgreementsAction({
                role: role as ROLE,
            }),
        );

    const handleSupplyOpen = () => {
        if (clientPool.version === 2) {
            return dispatch(
                showModalAction({
                    modalName: ModalName.POOLS_SUPPLY_V2_MODAL,
                    modalTitle: 'Supply Liquidity',
                    modalProps: {
                        tokenSymbol: leverageToken.symbol,
                        balance: leverageToken.userBalance,
                        currentApy: clientPool.apy,
                        totalDepositThreshold: clientPool.totalDepositThreshold,
                        agreementAddress: address,
                        totalBorrowed: clientPool.totalBorrowed,
                        tokenAddress: leverageToken.address,
                        totalAssets: clientPool.totalAssets,
                        assetsFetched: clientPool.assetsFetched,
                        isSupply: true,
                        decimals: leverageToken.decimals,
                        refresh: refreshAgreements,
                    },
                }),
            );
        }

        return dispatch(
            showModalAction({
                modalName: ModalName.POOLS_SUPPLY_MODAL,
                modalTitle: 'Supply Liquidity',
                modalProps: {
                    tokenSymbol: leverageToken.symbol,
                    balance: leverageToken.userBalance,
                    currentApy: clientPool.apy,
                    decimals: leverageToken.decimals,
                    totalDeposited: clientPool.totalDeposited,
                    totalDepositThreshold: clientPool.totalDepositThreshold,
                    poolUtilization: clientPool.poolUtilization,
                    agreementAddress: address,
                    tokenAddress: leverageToken.address,
                    refresh: refreshAgreements,
                },
            }),
        );
    };

    const handleBorrowOpen = () => {
        return dispatch(
            showModalAction({
                modalName: ModalName.BORROW_MODAL,
                modalTitle: `Borrow ${leverageToken?.symbol}`,
                modalProps: {
                    tokenSymbol: leverageToken?.symbol,
                    agreementAddress: address,
                    tokenAddress: leverageToken?.address,
                },
            }),
        );
    };

    const handleBtnClick = () => {
        switch (role) {
            case ROLE.LENDER:
                return handleSupplyOpen();
            case ROLE.BORROWER:
                return handleBorrowOpen();
            default:
                return;
        }
    };

    const handleEtherscan = () => {
        window.open(`https://etherscan.io/address/${address}`, '_blank');
    };

    const handleBack = () => {
        window.history.back();
    };

    return (
        <>
            {!type ||
            clientPoolLoading ||
            (pool && pool.address === '') ||
            !tokens.length ||
            !leverageToken ? (
                <div className={classes.loader}>
                    <Loader />
                </div>
            ) : (
                <div>
                    <div className={classes.top}>
                        <div className={classes.breadcrumbs}>
                            <Breadcrumb>
                                <BreadcrumbList>
                                    <BreadcrumbItem>
                                        <BreadcrumbLink onClick={handleBack}>Pools</BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbSeparator />
                                    <BreadcrumbItem>
                                        <BreadcrumbLink>
                                            <div
                                                className={classes.addressWrapper}
                                                data-testid='pool-address'
                                            >
                                                <div>
                                                    {pool.title}{' '}
                                                    <span>{`(${WalletHelper.addressShortName(
                                                        pool.address,
                                                    )})`}</span>
                                                </div>
                                                <TextButton
                                                    onClick={handleCopyAddress}
                                                    data-testid='pool-copy-address-button'
                                                    className={classes.textButton}
                                                >
                                                    <CopyIcon />
                                                </TextButton>
                                            </div>
                                        </BreadcrumbLink>
                                    </BreadcrumbItem>
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>
                        <div className={classes.heading}>
                            <div className={classes.content}>
                                <AssetIcon
                                    data-testid={`pool-icon-${leverageToken.symbol}`}
                                    assetName={leverageToken!.symbol}
                                    className='h-[40px] w-[40px]'
                                />
                                <div className={classes.title}>
                                    <div className={classes.titleText} data-testid='pool-title'>
                                        {pool.title}
                                    </div>
                                </div>
                                <div
                                    className={classes.etherscan}
                                    onClick={handleEtherscan}
                                    data-testid='etherscan'
                                >
                                    <Etherscan />
                                </div>
                            </div>
                            <div className={classes.buttonWrapper}>
                                <ButtonStroke
                                    isPrimary
                                    size={ButtonSize.md}
                                    className={classes.borrowButton}
                                    disabled={false}
                                    data-testid={'pool-supply-button'}
                                    onClick={handleBtnClick}
                                >
                                    {role === ROLE.LENDER ? 'Supply' : 'Borrow'}
                                </ButtonStroke>
                            </div>
                        </div>
                    </div>

                    <div className={classes.tabs}>
                        <PoolDetailsTabs />
                    </div>
                </div>
            )}
        </>
    );
};
